import { Mail, MapPin, Phone, ShieldCheck, Trash2, UserRoundCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";

const getUserLabel = (user) => user?.name || user?.fullName || "Unknown";

const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("/admin/users");
        setUsers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching users:", err);
        toast.error("Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers((prev) => prev.filter((user) => user._id !== userId));
      toast.success("User deleted successfully");
      setSelectedUser(null);
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error("Failed to delete user");
    }
  };

  const handleToggleVerify = async (userId, currentStatus) => {
    try {
      await api.patch(`/admin/users/${userId}/verify`, { verified: !currentStatus });
      setUsers((prev) =>
        prev.map((user) => (user._id === userId ? { ...user, verified: !currentStatus } : user))
      );
      toast.success("User verification updated");
    } catch (err) {
      console.error("Error updating user:", err);
      toast.error("Failed to update user");
    }
  };

  const filteredUsers = users.filter((user) => {
    const search = searchTerm.toLowerCase();
    const matchesSearch =
      getUserLabel(user).toLowerCase().includes(search) ||
      user.phone?.includes(searchTerm) ||
      user.email?.toLowerCase().includes(search);
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#ff9933]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Users</h2>
          <p className="text-sm text-slate-500">Search, verify, and control account access across the platform.</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8] sm:w-72"
          />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm focus:border-[#ff9933] focus:outline-none focus:ring-2 focus:ring-[#ffe1b8]"
          >
            <option value="all">All Roles</option>
            <option value="voter">Voters</option>
            <option value="candidate">Candidates</option>
            <option value="admin">Admins</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-[#d7dfd4] bg-white shadow-sm">
        <div className="grid gap-4 border-b border-slate-100 px-6 py-5 md:grid-cols-3">
          <div className="rounded-3xl bg-[#fff8ef] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b76500]">Visible users</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{filteredUsers.length.toLocaleString()}</p>
          </div>
          <div className="rounded-3xl bg-[#f3fbf4] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#106d06]">Verified</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{filteredUsers.filter((user) => user.verified).length.toLocaleString()}</p>
          </div>
          <div className="rounded-3xl bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Admin accounts</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{filteredUsers.filter((user) => user.role === "admin").length.toLocaleString()}</p>
          </div>
        </div>

        <div className="grid gap-4 p-6">
          {filteredUsers.map((user, index) => (
            <motion.div
              key={user._id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.025 }}
              className="rounded-[1.75rem] border border-slate-100 bg-[#fbfcfa] p-5"
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-gradient-to-br from-[#ff9933] via-white to-[#138808] text-lg font-semibold text-slate-900">
                    {getUserLabel(user).charAt(0) || "U"}
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">{getUserLabel(user)}</h3>
                      <span className="rounded-full bg-slate-900 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                        {user.role || "voter"}
                      </span>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${user.verified ? "bg-[#edf8ef] text-[#106d06]" : "bg-[#fff1df] text-[#b76500]"}`}>
                        {user.verified ? "Verified" : "Pending"}
                      </span>
                    </div>
                    <div className="grid gap-2 text-sm text-slate-600 md:grid-cols-2">
                      <p className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {user.phone || "N/A"}</p>
                      <p className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {user.email || "No email"}</p>
                      <p className="inline-flex items-center gap-2 md:col-span-2"><MapPin className="h-4 w-4 text-slate-400" /> {user.permanentAddress?.mlaConstituency || "N/A"}{user.permanentAddress?.state ? `, ${user.permanentAddress.state}` : ""}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedUser(user)}
                    className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => handleToggleVerify(user._id, user.verified)}
                    className="inline-flex items-center gap-2 rounded-2xl bg-[#138808] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#106d06]"
                  >
                    <UserRoundCheck className="h-4 w-4" />
                    {user.verified ? "Revoke" : "Verify"}
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user._id)}
                    className="inline-flex items-center gap-2 rounded-2xl bg-[#d95d39] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#c44c29]"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}

          {filteredUsers.length === 0 && (
            <div className="rounded-[1.75rem] border border-dashed border-slate-200 bg-slate-50 p-12 text-center text-slate-500">
              No users found matching your criteria.
            </div>
          )}
        </div>
      </div>

      {selectedUser && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedUser(null)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-100 bg-gradient-to-r from-[#fff1df] via-white to-[#edf8ef] px-6 py-5">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-[#ff9933] via-white to-[#138808] text-2xl font-semibold text-slate-900">
                  {getUserLabel(selectedUser).charAt(0) || "U"}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{getUserLabel(selectedUser)}</h3>
                  <p className="mt-1 text-sm uppercase tracking-[0.18em] text-slate-500">{selectedUser.role || "voter"}</p>
                </div>
              </div>
            </div>
            <div className="grid gap-4 p-6 text-sm text-slate-700 sm:grid-cols-2">
              <p className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {selectedUser.phone || "N/A"}</p>
              <p className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> {selectedUser.email || "No email"}</p>
              <p className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-slate-400" /> {selectedUser.verified ? "Verified" : "Pending"}</p>
              <p>User ID: {selectedUser._id}</p>
              <p className="sm:col-span-2">
                Address: {selectedUser.permanentAddress?.addressLine1 || "N/A"}
              </p>
              <p>District: {selectedUser.permanentAddress?.district || "N/A"}</p>
              <p>State: {selectedUser.permanentAddress?.state || "N/A"}</p>
              <p>MLA Constituency: {selectedUser.permanentAddress?.mlaConstituency || "N/A"}</p>
              <p>MP Constituency: {selectedUser.permanentAddress?.mpConstituency || "N/A"}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default UserManagementPanel;
